import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiArrowLeft, FiCheckCircle, FiLoader, FiAlertCircle, FiClock, FiShield } = FiIcons;

const RESET_ATTEMPTS_KEY = 'sohlocal_reset_attempts';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const { resetPassword } = useSupabaseAuth();
  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const checkRateLimit = () => {
    const now = Date.now();
    const attempts = JSON.parse(localStorage.getItem(RESET_ATTEMPTS_KEY) || '[]');
    const oneHourAgo = now - 3600000;
    
    // Filter attempts from the last hour
    const recentAttempts = attempts.filter(time => time > oneHourAgo);
    
    if (recentAttempts.length >= 3) {
      const waitTime = Math.ceil((recentAttempts[0] + 3600000 - now) / 60000);
      return `Limit reached. Please wait ${waitTime} minutes before requesting another code.`;
    }
    return null;
  };

  const logAttempt = () => {
    const attempts = JSON.parse(localStorage.getItem(RESET_ATTEMPTS_KEY) || '[]');
    attempts.push(Date.now());
    localStorage.setItem(RESET_ATTEMPTS_KEY, JSON.stringify(attempts.slice(-5)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError('Please complete the security verification.');
      return;
    }

    const rateLimitError = checkRateLimit();
    if (rateLimitError) {
      setError(rateLimitError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: resetError } = await resetPassword(email, turnstileToken);
      if (resetError) throw resetError;
      
      logAttempt();
      // Redirect to PIN entry page
      navigate('/reset-password', { state: { email } });
    } catch (err) {
      console.error('Reset error:', err);
      setError(err.message || 'Failed to send recovery code.');
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileKey((currentKey) => currentKey + 1);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 1 }} className="max-w-md w-full space-y-8 bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-gray-100">
        <div className="text-center">
          <div className="w-20 h-20 bg-indigo-600 rounded-3xl flex items-center justify-center p-4 mx-auto mb-6 shadow-xl shadow-indigo-100/50">
            <SafeIcon icon={FiShield} className="text-white text-4xl" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Recover Account</h2>
          <p className="mt-2 text-sm text-slate-500 font-medium">We'll send a 6-digit PIN to your email</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-2xl flex items-start text-sm">
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
              <span className="font-bold">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Email Address</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SafeIcon icon={FiMail} className="text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
              </div>
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="pl-11 block w-full px-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-medium" 
                placeholder="name@example.com" 
              />
            </div>
          </div>

          <div className="flex justify-center py-2">
            <Turnstile key={turnstileKey} siteKey={SITE_KEY} onSuccess={(token) => { setTurnstileToken(token); setError(null); }} onExpire={() => setTurnstileToken(null)} />
          </div>

          <button 
            type="submit" 
            disabled={loading || !turnstileToken} 
            className="w-full flex justify-center py-4 px-4 bg-indigo-600 text-white text-sm font-black rounded-2xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-xl shadow-indigo-100 transform active:scale-[0.98]"
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin mr-2" /> : 'Send Recovery Code'}
          </button>

          <div className="text-center">
            <Link to="/login" className="inline-flex items-center text-sm font-black text-gray-400 hover:text-indigo-600 transition-colors">
              <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Sign In
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
